import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  PlanningPeriodsDataCollectionDocument,
  PlanningValueObjectsPeriodTypeEnum,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'
import {
  getPeriodStatusLabel,
  PLANNING_PERIOD_STATUS,
  PLANNING_PERIOD_STATUS_COLORS,
} from 'modules/planning/consts/period_status'
import {
  getRequestStatusLabel,
  REQUEST_STATUS_COLORS,
} from 'modules/planning/consts/request_status'
import {
  formatPeriod,
  getRequestDeadline,
  getRequestStatus,
} from 'modules/planning/lib/periodHelpers'

import { PlanningPeriodNode } from 'modules/planning/types/queries'

const getStatusFilterOptions = () =>
  Object.entries(PLANNING_PERIOD_STATUS).map(([, value]) => ({
    label: getPeriodStatusLabel(value),
    value,
  }))

interface PeriodsListProps {
  periodType: PlanningValueObjectsPeriodTypeEnum
  itemUrl: (item: PlanningPeriodNode) => string
}

const PeriodsList = ({ periodType, itemUrl }: PeriodsListProps) => {
  const { company } = useCurrentSession()

  const source = useGraphqlDataCollectionSource(
    {
      document: PlanningPeriodsDataCollectionDocument,
      mapData: (data) => {
        const edges = data.planning.periodsConnection?.edges ?? []
        return edges.map((edge) => edge.node).filter(Boolean)
      },
      mapVariables: ({ filters }) => ({
        companyId: company.id,
        periodTypes: [periodType],
        statuses: filters?.status && filters.status.length > 0 ? filters.status : undefined,
      }),
      mapPagination: (data) => ({
        total: data.planning.periodsConnection?.totalCount ?? 0,
      }),
      paginationType: 'pages',
      search: {
        enabled: true,
      },
      itemUrl,
      filters: {
        status: {
          label: i18n.t('planning.periods_page.columns.status'),
          type: 'in',
          options: {
            options: getStatusFilterOptions(),
          },
        },
      },
    },
    [company.id, periodType]
  )

  return (
    <OneDataCollection
      source={source}
      emptyStates={{
        'no-data': {
          title: i18n.t('planning.periods_page.empty_state.title'),
          description: i18n.t('planning.periods_page.empty_state.description'),
        },
      }}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                label: i18n.t('planning.periods_page.columns.name'),
                render: (item: PlanningPeriodNode) => ({
                  type: 'text' as const,
                  value: item.name,
                }),
              },
              {
                label: i18n.t('planning.periods_page.columns.period'),
                render: (item: PlanningPeriodNode) => ({
                  type: 'text' as const,
                  value: formatPeriod(item),
                }),
              },
              {
                label: i18n.t('planning.periods_page.columns.status'),
                render: (item: PlanningPeriodNode) => ({
                  type: 'status' as const,
                  value: {
                    label: getPeriodStatusLabel(item.status),
                    status: PLANNING_PERIOD_STATUS_COLORS[item.status],
                  },
                }),
              },
              {
                label: i18n.t('planning.periods_page.columns.request_deadline'),
                render: (item: PlanningPeriodNode) => ({
                  type: 'text' as const,
                  value: getRequestDeadline(item) ?? '',
                }),
              },
              {
                label: i18n.t('planning.periods_page.columns.request_status'),
                render: (item: PlanningPeriodNode) => {
                  const requestStatus = getRequestStatus(item)

                  return {
                    type: 'status' as const,
                    value: {
                      status: REQUEST_STATUS_COLORS[requestStatus],
                      label: getRequestStatusLabel(requestStatus),
                    },
                  }
                },
              },
            ],
          },
        },
      ]}
    />
  )
}

export default PeriodsList
